// https://github.com/auth0/nextjs-auth0/blob/main/V4_MIGRATION_GUIDE.md
//
// WARN:
// auth0には
// 1. browser <=> auth0間のセッション
// 2. browser <=> app間(nitoelでいうとnextjs)のセッション
// の2つがある。
// 両方のセッションの有効期限を同じように設定しないと、UXとしておかしなことになるので
// 必ず2つの有効期限を同じように設定すること。
// 1. browser <=> auth0間のセッション: terrafromで設定してる
// 2. browser <=> app間(nitoelでいうとnextjs)のセッション: tenant-frontend/lib/auth0.tsxで設定してる
import { Auth0Client } from "@auth0/nextjs-auth0/server";

import { enableAccessTokenEndpointFor } from "lib/authEnvironment";

// v4.17.0以降、domainパラメータが必須になったため、環境変数から取得する
// ビルド時にはダミー値が設定される
export const auth0 = new Auth0Client({
  domain: process.env.AUTH0_DOMAIN || "dummy.auth0.com",
  // セッション Cookie を 1 本に収めるため、保存前にユーザークレームを絞る。
  //
  // 4096 バイトを超えると nextjs-auth0 は `__session__0` / `__session__1` に分割する。
  // `rolling: true` でリクエストごとにセッションを書き直すので、**複数の応答が同時に別々の値で
  // 分割 Cookie を書くと、ブラウザ側で組み合わせが混ざって復号できなくなる**。その状態で
  // `getAccessToken()` が throw し、BFF（app/api/tenant-api-proxy）が本文空の 500 を返す
  // （dev で「サイドバーを開くと 2 回に 1 回 処理に失敗しました」として発生。落ちた応答が
  // 両方の chunk を書き直すため、開き直すと通る、という症状だった）。
  //
  // アプリはユーザー情報を GraphQL の `me` から取得しており、**この `user` を読んでいる箇所は無い**
  // （Auth0 セッションの利用は `auth0.getAccessToken()` だけ）。`sub` は本人の識別子として必須、
  // `email` / `org_id` は運用でセッションを覗くときの手掛かりとして残し、name / picture /
  // 各種メタデータは落とす。
  //
  // **id トークンも保存しない。** クレームを絞っただけでは 4096 バイトを 120 バイトほど超えたままで
  // （dev 実測: 2 本目の chunk が 660 → 120 文字に減ったが分割は解消しなかった）、トークン長は
  // 組織クレームやメール長で変動するため、境界ぎりぎりでは再発する。id トークンを外すと 1KB 前後の
  // 余裕ができる。
  //
  // 外して困るのは logout の `id_token_hint` だけで、**無くてもログアウトは成立する**
  // （nextjs-auth0 の実装は `session?.tokenSet.idToken` がある場合のみ付与し、URL には
  // `client_id` と `logout_hint`（sid）も載る）。access / refresh トークンは当然そのまま保持する。
  beforeSessionSaved: async (session) => ({
    ...session,
    user: {
      sub: session.user.sub,
      ...(session.user.email === undefined ? {} : { email: session.user.email }),
      ...(session.user.org_id === undefined ? {} : { org_id: session.user.org_id }),
    },
    tokenSet: { ...session.tokenSet, idToken: undefined },
  }),
  // `/auth/access-token` を **dev 以外で閉じる**。
  //
  // nextjs-auth0 v4 の既定は true（`enableAccessTokenEndpoint ?? true`）で、prod を含む全環境で
  // この口が開いている。フロントは BFF（app/api/tenant-api-proxy）経由で API を叩いており、
  // **この口を呼んでいる画面コードは無い**ため、閉じても挙動は変わらない。
  //
  // **これでトークン取得経路が塞がるわけではない**: 同等の自作口 pages/api/get_access_token.tsx が
  // 別に存在し、そちらは残る（別対応）。ここで閉じるのは「使われていないのに開いている口」だけ。
  //
  // 開けておく必要があるのは、ブラウザからおまかせエージェント（dev 限定・Phase 3）へ直結するときだけ。
  // 判定は純関数に切り出してテストしている（未設定は閉じる側へ倒す）。
  enableAccessTokenEndpoint: enableAccessTokenEndpointFor(process.env.NEXT_PUBLIC_KIND),
  authorizationParameters: {
    audience: "https://nitoel-api",
    scope: "openid profile email offline_access",
  },
  session: {
    rolling: true,
    // Integer value, in seconds, for application absolute rolling duration.
    // The amount of time after the user has logged in that they will be logged out
    absoluteDuration: 30 * 24 * 60 * 60, // 30 days
    // Integer value, in seconds, for application session inactivity duration.
    // The amount of time for which the user must be idle for then to be logged out
    inactivityDuration: 7 * 24 * 60 * 60, // 7 days
  },
});
